/**
 * ADVENTURES DATA - SINGLE SOURCE OF TRUTH
 *
 * Este archivo contiene la configuración centralizada de todas las adventures.
 * IMPORTANTE: Las imágenes hero aquí se sincronizan automáticamente con:
 * - Carrusel de la homepage (Adventures.tsx)
 * - Página individual de cada adventure
 *
 * Cambiar la imagen hero aquí actualiza ambos lugares automáticamente.
 */

export interface Adventure {
  id: string
  title: string
  shortTitle?: string // Título corto para carrusel si es necesario
  description: string
  heroImage: string // Imagen hero - usada en homepage carousel Y página individual
  link: string
  subtitle?: string // Subtítulo para HeroImage
  location?: string // Ubicación para HeroImage
  blockedDates?: string[] // Admin-blocked dates (YYYY-MM-DD format)
  hidden?: boolean // Hide adventure from public display
  bookingMode?: 'direct' | 'whatsapp' | 'hidden'
}

export const adventures: Adventure[] = [
  {
    id: "resort-skiing",
    title: "La Parva & Valle Nevado",
    shortTitle: "Resort Skiing",
    description: "Experience world-class resort skiing at premier destinations. From the powder-filled slopes of the Andes to exclusive access runs, enjoy the perfect blend of luxury and alpine adventure.",
    heroImage: "/images/adventures/resort-skiing/resort-skiing-hero-1280.webp?v=2",
    link: "/guided-adventures/resort-skiing",
    subtitle: "Premium experiences at Chile's most exclusive resorts. Tres Valles System",
    location: "Andes • Chile",
    bookingMode: 'direct'
  },
  {
    id: "heli-skiing",
    title: "Heli-skiing",
    description: "Our heli operations access some of the world's most pristine ski terrain. From Iceland to Patagonia, drop into untracked open faces and steeps with skilled guides.",
    heroImage: "/images/adventures/heli-skiing/heli-skiing-hero-1920.webp?v=2",
    link: "/guided-adventures/heli-skiing",
    subtitle: "Untracked powder descents in the world's most remote terrain",
    location: "Iceland • Patagonia • Alaska",
    bookingMode: 'whatsapp'
  },
  {
    id: "ski-touring",
    title: "Ski Touring",
    description: "Venture into the backcountry with expert guides on unforgettable ski touring expeditions. Discover pristine landscapes and earn your turns on remote peaks with stunning views.",
    heroImage: "/images/adventures/ski-touring/ski-touring-1280.webp?v=2",
    link: "/guided-adventures/ski-touring",
    subtitle: "Earn your turns in pristine backcountry terrain",
    location: "Alps • Andes • Rockies",
    bookingMode: 'direct'
  },
  {
    id: "cat-skiing",
    title: "Cat Skiing",
    description: "Experience day after day of the deepest, fluffiest powder in Colorado at our exclusive cat-skiing tenure, accessed directly from our Crested Butte lodges.",
    heroImage: "/images/adventures/cat-skiing/cat-skiing-1280.webp",
    link: "/guided-adventures/cat-skiing",
    subtitle: "Exclusive cat-skiing terrain accessed from our Crested Butte lodges",
    location: "Colorado • USA",
    hidden: true,
    bookingMode: 'whatsapp'
  },
  {
    id: "fly-fishing",
    title: "Fly Fishing",
    description: "From sight fishing for browns on the South Island to targeting Atlantic salmon on the Hölkná, we deliver the best guides, gear, and access in New Zealand, Iceland, Patagonia, Colorado, and The Bahamas.",
    heroImage: "/images/adventures/fly-fishing/fly-fishing-hero-1280.webp?v=2",
    link: "/guided-adventures/fly-fishing",
    subtitle: "World-class fly fishing in pristine waters",
    location: "Patagonia • Iceland • New Zealand",
    bookingMode: 'direct'
  },
  {
    id: "water-skiing",
    title: "Water Skiing",
    description: "Glide across pristine waters in breathtaking locations. Whether you're a beginner or expert, our professional instructors and premium equipment ensure an exhilarating water skiing experience.",
    heroImage: "/images/adventures/water-skiing/water-skiing-hero-1280.webp",
    link: "/guided-adventures/water-skiing",
    subtitle: "Premium water skiing experiences in stunning locations",
    location: "Patagonia • Colorado",
    hidden: true,
    bookingMode: 'whatsapp'
  }
]

// Helper function para obtener adventure por ID
export function getAdventureById(id: string): Adventure | undefined {
  return adventures.find(adventure => adventure.id === id)
}

// Helper function para obtener solo las imágenes hero
export function getAdventureHeroImages(): Record<string, string> {
  return adventures.reduce((acc, adventure) => {
    acc[adventure.id] = adventure.heroImage
    return acc
  }, {} as Record<string, string>)
}
